import React, { createContext, useContext, useState } from "react";
import Snackbar, {
  SnackbarSeverity,
  SnackbarPosition,
} from "../components/snackbar";

interface SnackbarContextType {
  showSnackbar: (
    message: string,
    severity?: SnackbarSeverity,
    position?: SnackbarPosition,
  ) => void;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(
  undefined,
);

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error("useSnackbar must be used within a SnackbarProvider");
  }
  return context;
};

interface SnackbarProviderProps {
  children: React.ReactNode;
}

export const SnackbarProvider: React.FC<SnackbarProviderProps> = ({
  children,
}) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState<SnackbarSeverity>("info");
  const [position, setPosition] = useState<SnackbarPosition>("bottom-right");

  const showSnackbar = (
    newMessage: string,
    newSeverity: SnackbarSeverity = "info",
    newPosition: SnackbarPosition = "bottom-right",
  ) => {
    setMessage(newMessage);
    setSeverity(newSeverity);
    setPosition(newPosition);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      {children}
      <Snackbar
        open={open}
        message={message}
        severity={severity}
        onClose={handleClose}
        position={position}
      />
    </SnackbarContext.Provider>
  );
};
